import React, { createContext, useCallback, useContext, useMemo } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../api/axiosInstance";
// Every read still runs off static JSON for now — no axios instance backs
// them. To restore the real backend for a given lookup, swap its
// `resolveLocal(...)` call below back to `request(functionName, body)`, see
// the commented reference implementation at the bottom of this file.
//
// submitCareerApplication is the one write already wired to the real
// backend (see CAREER_APPLY_API.md for the payload contract).

import homeData from "../data/home.json";
import newsData from "../data/news.json";
import newsMedia from "../data/news-media";
import careerData from "../data/career.json";
import contactData from "../data/contact.json";
import facultyData from "../data/faculty.json";
import departmentsData from "../data/departments.json";
import programsData from "../data/programs.json";
import admissionData from "../data/admission.json";
import seatAllocationData from "../data/seat-allocation.json";

import csDepartmentFaculty from "../data/faculty/computer-science.json";
import electricalEngineeringFaculty from "../data/faculty/electrical-engineering.json";
import electronicEngineeringFaculty from "../data/faculty/electronic-engineering.json";
import basicSciencesFaculty from "../data/faculty/basic-sciences.json";
import agronomyFaculty from "../data/faculty/agronomy.json";
import mathematicsFaculty from "../data/faculty/mathematics.json";
import civilEngineeringFaculty from "../data/faculty/civil-engineering.json";
import mechanicalEngineeringFaculty from "../data/faculty/mechanical-engineering.json";
import managementAdminSciencesFaculty from "../data/faculty/management-admin-sciences.json";
import accountingEconomicsFaculty from "../data/faculty/accounting-economics.json";
import englishLanguageLiteratureFaculty from "../data/faculty/english-language-literature.json";
import soilScienceFaculty from "../data/faculty/soil-science.json";
import plantPathologyFaculty from "../data/faculty/plant-pathology.json";
import entomologyFaculty from "../data/faculty/entomology.json";
import plantBreedingGeneticsFaculty from "../data/faculty/plant-breeding-genetics.json";
import vcOfficeFaculty from "../data/faculty/vc-office.json";
import registrarOfficeFaculty from "../data/faculty/registrar-office.json";
import sfaoFaculty from "../data/faculty/sfao.json";
import oricFaculty from "../data/faculty/oric.json";
import qecFaculty from "../data/faculty/qec.json";
import samcFaculty from "../data/faculty/samc.json";
import directorFinanceFaculty from "../data/faculty/director-finance.json";
import networkExchangeFaculty from "../data/faculty/network-exchange.json";
import controllerExaminationsFaculty from "../data/faculty/controller-examinations.json";
import libraryOfficeFaculty from "../data/faculty/library-office.json";
import residentAuditorFaculty from "../data/faculty/resident-auditor.json";
import sportSectionFaculty from "../data/faculty/sport-section.json";
import securityEstateFaculty from "../data/faculty/security-estate.json";
import industrialLiaisonFaculty from "../data/faculty/industrial-liaison.json";
import hostelSectionFaculty from "../data/faculty/hostel-section.json";
import admissionOfficeFaculty from "../data/faculty/admission-office.json";
import focalPersonOfficeFaculty from "../data/faculty/focal-person-office.json";
import profileDrSajjadAhmedBhatti from "../data/faculty/profiles/dr-sajjad-ahmed-bhatti.json";
import profileTariqueAhmedMemon from "../data/faculty/profiles/tarique-ahmed-memon.json";
import profileKhalidHussainSolangi from "../data/faculty/profiles/khalid-hussain-solangi.json";
import profileAbidAliShah from "../data/faculty/profiles/abid-ali-shah.json";
import profileAsifAliLaghari from "../data/faculty/profiles/asif-ali-laghari.json";
import profileZulfiqarAliAbbasi from "../data/faculty/profiles/zulfiqar-ali-abbasi.json";
import profileQurbanAliShah from "../data/faculty/profiles/qurban-ali-shah.json";
import profileMusratBegumAbro from "../data/faculty/profiles/musrat-begum-abro.json";
import profileKhalilJibranAbbasi from "../data/faculty/profiles/khalil-jibran-abbasi.json";
import profileAyazAliMaitlo from "../data/faculty/profiles/ayaz-ali-maitlo.json";
import profileAtharAliGorar from "../data/faculty/profiles/athar-ali-gorar.json";
import profileAfaqueAhmedBhutto from "../data/faculty/profiles/afaque-ahmed-bhutto.json";
import profileKhalilMuhammadZuhaib from "../data/faculty/profiles/khalil-muhammad-zuhaib.json";
import profileJaveedShabbirDar from "../data/faculty/profiles/javeed-shabbir-dar.json";
import profileSaeedAhmedRajput from "../data/faculty/profiles/saeed-ahmed-rajput.json";
import profileAhmedFarazAbro from "../data/faculty/profiles/ahmed-faraz-abro.json";
import profileMukhtiarAhmedShar from "../data/faculty/profiles/mukhtiar-ahmed-shar.json";
import profileAneelaPathan from "../data/faculty/profiles/aneela-pathan.json";
import profileAliRazaRadhan from "../data/faculty/profiles/ali-raza-radhan.json";
import profileAyazAhmedSoomro from "../data/faculty/profiles/ayaz-ahmed-soomro.json";
import profileHaseebURehman from "../data/faculty/profiles/haseeb-u-rehman.json";
import profileIrfanAliTunio from "../data/faculty/profiles/irfan-ali-tunio.json";
import profileUmairSaeedSolangi from "../data/faculty/profiles/umair-saeed-solangi.json";
import profileGhulamHussainChandio from "../data/faculty/profiles/ghulam-hussain-chandio.json";
import profileFareesaKhanSohu from "../data/faculty/profiles/fareesa-khan-sohu.json";
import profileAhmedPanhwar from "../data/faculty/profiles/ahmed-panhwar.json";
import profileMohsinShaikh from "../data/faculty/profiles/mohsin-shaikh.json";
import profileTufailAhmedWagan from "../data/faculty/profiles/tufail-ahmed-wagan.json";
import profileYasirAliBhutto from "../data/faculty/profiles/yasir-ali-bhutto.json";
import profileAbdulSattar from "../data/faculty/profiles/abdul-sattar.json";
import profileSaweraSohu from "../data/faculty/profiles/sawera-sohu.json";
import profileSanaAli from "../data/faculty/profiles/sana-ali.json";
import profileFarooqueAhmed from "../data/faculty/profiles/farooque-ahmed.json";
import profileShaguftaSaleem from "../data/faculty/profiles/shagufta-saleem.json";
import profileNusratHussain from "../data/faculty/profiles/nusrat-hussain.json";

import peopleData from "../data/people.json";
import officersData from "../data/officers.json";
import admissionPolicyData from "../data/admission-policy.json";
import facilitiesData from "../data/facilities.json";
import feesData from "../data/fees.json";
import testimonialsData from "../data/testimonials.json";

import { withCareerMeta } from "../utils/career";

const ApiContext = createContext(null);

// Set to 0 to resolve instantly. A small delay keeps skeleton loaders visible
// during local development so loading states can still be reviewed/designed.
const SIMULATED_DELAY_MS = 250;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// "DD Mon YYYY" (e.g. "07 Aug 2026") sorted newest-first. Used for the
// homepage Updates preview and the full /Updates listing so both stay in
// sync instead of relying on whatever order the JSON happens to be in.
const sortByDateDesc = (items, dateField = "EVENT_DATE") =>
  [...items].sort((a, b) => new Date(b[dateField]) - new Date(a[dateField]));

// News stories carry no image fields of their own — the gallery comes from
// whatever is sitting in public/images/news/<SLUG>/, captured at build time in
// the generated news-media manifest. The banner/card image is simply the first
// file in that folder, so adding, removing or reordering images needs no edit
// here or in news.json.
const NEWS_FALLBACK_IMG = "/images/placeholder.svg";

const withNewsMedia = (story) => {
  const gallery = newsMedia[story.SLUG] || [];
  return { ...story, EVENT_GALLERY: gallery, EVENT_IMG: gallery[0] || NEWS_FALLBACK_IMG };
};

// Career openings are decorated once, here, rather than in each page: the
// listing card, the detail page and the apply form all read the same derived
// `isClosed` / `SLUG`, so a card can never offer an Apply button that the
// apply page then rejects. Open postings sort ahead of expired ones.
const CAREER_OPENINGS = careerData
  .filter((job) => job.MSG === "Success")
  .map(withCareerMeta)
  .sort((a, b) => Number(a.isClosed) - Number(b.isClosed));

const findDeptRaw = (id) => departmentsData.find((d) => d.DeptID === id);
const findDept = (id) => {
  const dept = findDeptRaw(id);
  return dept && dept.CHAIRMAN ? { ...dept, CHAIRMAN: withPerson(dept.CHAIRMAN) } : dept;
};

// Faculty directory data model (task: JSON-driven Faculty Listing/Profile pages).
// Each department's roster lives in its own `faculty/[slug].json`; the DeptID
// -> slug link lives on the department record itself (`ROSTER_SLUG`), so a new
// department only needs a data file drop plus a ROSTER_SLUG value, no code change.
const FACULTY_ROSTER_BY_SLUG = {
  "computer-science": csDepartmentFaculty,
  "electrical-engineering": electricalEngineeringFaculty,
  "electronic-engineering": electronicEngineeringFaculty,
  "basic-sciences": basicSciencesFaculty,
  agronomy: agronomyFaculty,
  mathematics: mathematicsFaculty,
  "civil-engineering": civilEngineeringFaculty,
  "mechanical-engineering": mechanicalEngineeringFaculty,
  "management-admin-sciences": managementAdminSciencesFaculty,
  "accounting-economics": accountingEconomicsFaculty,
  "english-language-literature": englishLanguageLiteratureFaculty,
  "soil-science": soilScienceFaculty,
  "plant-pathology": plantPathologyFaculty,
  entomology: entomologyFaculty,
  "plant-breeding-genetics": plantBreedingGeneticsFaculty,
  "vc-office": vcOfficeFaculty,
  "registrar-office": registrarOfficeFaculty,
  sfao: sfaoFaculty,
  oric: oricFaculty,
  qec: qecFaculty,
  samc: samcFaculty,
  "director-finance": directorFinanceFaculty,
  "network-exchange": networkExchangeFaculty,
  "controller-examinations": controllerExaminationsFaculty,
  "library-office": libraryOfficeFaculty,
  "resident-auditor": residentAuditorFaculty,
  "sport-section": sportSectionFaculty,
  "security-estate": securityEstateFaculty,
  "industrial-liaison": industrialLiaisonFaculty,
  "hostel-section": hostelSectionFaculty,
  "admission-office": admissionOfficeFaculty,
  "focal-person-office": focalPersonOfficeFaculty,
};
const FACULTY_PROFILE_BY_SLUG = {
  "dr-sajjad-ahmed-bhatti": profileDrSajjadAhmedBhatti,
  "tarique-ahmed-memon": profileTariqueAhmedMemon,
  "khalid-hussain-solangi": profileKhalidHussainSolangi,
  "abid-ali-shah": profileAbidAliShah,
  "asif-ali-laghari": profileAsifAliLaghari,
  "zulfiqar-ali-abbasi": profileZulfiqarAliAbbasi,
  "qurban-ali-shah": profileQurbanAliShah,
  "musrat-begum-abro": profileMusratBegumAbro,
  "khalil-jibran-abbasi": profileKhalilJibranAbbasi,
  "ayaz-ali-maitlo": profileAyazAliMaitlo,
  "athar-ali-gorar": profileAtharAliGorar,
  "afaque-ahmed-bhutto": profileAfaqueAhmedBhutto,
  "khalil-muhammad-zuhaib": profileKhalilMuhammadZuhaib,
  "javeed-shabbir-dar": profileJaveedShabbirDar,
  "saeed-ahmed-rajput": profileSaeedAhmedRajput,
  "ahmed-faraz-abro": profileAhmedFarazAbro,
  "mukhtiar-ahmed-shar": profileMukhtiarAhmedShar,
  "aneela-pathan": profileAneelaPathan,
  "ali-raza-radhan": profileAliRazaRadhan,
  "ayaz-ahmed-soomro": profileAyazAhmedSoomro,
  "haseeb-u-rehman": profileHaseebURehman,
  "irfan-ali-tunio": profileIrfanAliTunio,
  "umair-saeed-solangi": profileUmairSaeedSolangi,
  "ghulam-hussain-chandio": profileGhulamHussainChandio,
  "fareesa-khan-sohu": profileFareesaKhanSohu,
  "ahmed-panhwar": profileAhmedPanhwar,
  "mohsin-shaikh": profileMohsinShaikh,
  "tufail-ahmed-wagan": profileTufailAhmedWagan,
  "yasir-ali-bhutto": profileYasirAliBhutto,
  "abdul-sattar": profileAbdulSattar,
  "sawera-sohu": profileSaweraSohu,
  "sana-ali": profileSanaAli,
  "farooque-ahmed": profileFarooqueAhmed,
  "shagufta-saleem": profileShaguftaSaleem,
  "nusrat-hussain": profileNusratHussain,
};

// Single source of truth for a real person's identity (name + photo). Every
// roster entry / CHAIRMAN / DEAN / officer / profile record below stores only
// a `personId` (plus a `statusNote` for on-leave-style annotations); this
// resolves it back to the shape components already expect, so a name or
// photo only ever needs correcting in one place instead of drifting across
// however many listings that person appears in.
const PEOPLE_BY_ID = Object.fromEntries(peopleData.map((p) => [p.id, p]));
const withPerson = (record, { nameField = "name", photoField = "photo" } = {}) => {
  if (!record || !record.personId) return record;
  const { personId, statusNote, ...rest } = record;
  const person = PEOPLE_BY_ID[personId];
  return {
    ...rest,
    [nameField]: statusNote ? `${person?.name} (${statusNote})` : person?.name,
    [photoField]: person?.photo,
  };
};

// Global search index (task: site-wide search by faculty/department/program/
// person name). Built once from the same static data every other lookup
// above already uses, so a name only ever needs correcting in one place
// (people.json) to stay in sync everywhere, including here. Every entry
// carries a ready-to-navigate `path` matching how each page reads its query
// param (?id=, ?slug=, ?deptId=) elsewhere in this file.
const buildSearchIndex = () => {
  const entries = [];
  const deptPath = (dept) => (dept?.IsAcademic === "1" ? "/DptHome" : "/AdmHome") + `?id=${dept.DeptID}`;

  departmentsData.forEach((dept) => {
    entries.push({ type: "Department", title: dept.DeptTitle, path: deptPath(dept) });
    if (dept.CHAIRMAN?.personId) {
      const person = withPerson(dept.CHAIRMAN);
      if (person?.name) {
        entries.push({
          type: "Person",
          title: person.name,
          subtitle: `${dept.CHAIRMAN.title || "Chairman"}, ${dept.DeptTitle}`,
          path: deptPath(dept),
        });
      }
    }
  });

  programsData.forEach((program) => {
    entries.push({
      type: "Program",
      title: program.PGM_TITLE,
      subtitle: program.DEGREE_TYPE,
      path: `/ProHome?id=${program.PGM_ID}`,
    });
  });

  facultyData.forEach((faculty) => {
    entries.push({ type: "Faculty", title: faculty.FACULTY_NAME, path: `/FacHome?id=${faculty.FACULTY_ID}` });
    if (faculty.DEAN?.personId) {
      const person = withPerson(faculty.DEAN);
      if (person?.name) {
        entries.push({
          type: "Person",
          title: person.name,
          subtitle: `${faculty.DEAN.title || "Dean"}, ${faculty.FACULTY_NAME}`,
          path: `/FacHome?id=${faculty.FACULTY_ID}`,
        });
      }
    }
  });

  Object.entries(FACULTY_ROSTER_BY_SLUG).forEach(([slug, roster]) => {
    const dept = departmentsData.find((d) => d.ROSTER_SLUG === slug);
    if (!dept) return;
    roster.forEach((raw) => {
      const person = withPerson(raw);
      if (!person?.name) return;
      entries.push({
        type: "Person",
        title: person.name,
        subtitle: `${raw.designation || "Faculty"} · ${dept.DeptTitle}`,
        path: raw.profileSlug
          ? `/FacultyProfile?slug=${raw.profileSlug}&deptId=${dept.DeptID}`
          : `/Faculty?id=${dept.DeptID}`,
      });
    });
  });

  officersData.forEach((officer) => {
    if (!officer.profileSlug) return;
    const person = withPerson(officer, { nameField: "NAME", photoField: "PHOTO" });
    if (!person?.NAME) return;
    entries.push({
      type: "Person",
      title: person.NAME,
      subtitle: officer.TITLE,
      path: `/LeadershipProfile?slug=${officer.profileSlug}`,
    });
  });

  // A person can legitimately show up in several source records (roster
  // entry, department CHAIRMAN, faculty DEAN, officers.json) if they hold
  // more than one role, but surfacing every one of those as a separate
  // search result reads as a bug ("why is the same name listed 3 times?").
  // Collapse to a single result per name, keeping whichever record points
  // at their most specific/authoritative page.
  const PERSON_PAGE_RANK = ["/LeadershipProfile", "/FacultyProfile", "/DptHome", "/FacHome", "/AdmHome"];
  const personRank = (entry) => {
    const rank = PERSON_PAGE_RANK.findIndex((prefix) => entry.path.startsWith(prefix));
    return rank === -1 ? PERSON_PAGE_RANK.length : rank;
  };

  const bestPersonByName = new Map();
  entries.forEach((e) => {
    if (e.type !== "Person") return;
    const rank = personRank(e);
    const existing = bestPersonByName.get(e.title);
    if (!existing || rank < existing.rank) bestPersonByName.set(e.title, { entry: e, rank });
  });

  const merged = [...entries.filter((e) => e.type !== "Person"), ...[...bestPersonByName.values()].map((v) => v.entry)];

  // Dedupe identical (title, path) pairs, e.g. two departments that happen
  // to share a title.
  const seen = new Set();
  return merged.filter((e) => {
    const key = `${e.type}|${e.title}|${e.path}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SEARCH_INDEX = buildSearchIndex();

export const ApiProvider = ({ children }) => {
  // Mirrors the shape of the old axios-backed `request()` helper: same
  // try/catch, same centralized toast-with-dedupe, same `silent` opt-out,
  // same "throw on failure" contract, so callers (and a future swap back to
  // real APIs) don't need to change at all. `getData` may be a plain value or
  // a function (used for parameterized/computed lookups).
  const resolveLocal = useCallback(async (getData, { silent = false } = {}) => {
    try {
      if (SIMULATED_DELAY_MS > 0) await wait(SIMULATED_DELAY_MS);
      return typeof getData === "function" ? getData() : getData;
    } catch (error) {
      console.error("Static data resolution failed:", error);
      if (!silent) {
        toast.error("Something went wrong while loading data. Please try again.", { id: "api-error" });
      }
      throw error;
    }
  }, []);

  const api = useMemo(
    () => ({
      // Navigation / shared
      getDeptName: (id) => resolveLocal(() => (findDept(id) ? [findDept(id)] : [])),
      getDeptFacultyName: (id) => resolveLocal(() => (findDept(id) ? [findDept(id)] : [])),
      getFacultyList: () =>
        resolveLocal(() => facultyData.map((f) => ({ FACULTY_ID: f.FACULTY_ID, FACULTY_NAME: f.FACULTY_NAME }))),
      getAdminDepartments: () =>
        resolveLocal(() =>
          departmentsData.filter((d) => d.isAdminMenu).map((d) => ({ DeptID: d.DeptID, DeptTitle: d.DeptTitle }))
        ),
      getContactInfo: () => resolveLocal(() => [contactData]),
      getFacultyPrograms: (id) =>
        resolveLocal(
          () =>
            departmentsData
              .filter((d) => d.FACULTY_ID === id)
              .map((d) => ({ DeptTitle: d.DeptTitle, DPT_ID: d.DeptID })),
          { silent: true }
        ),
      getSearchIndex: () => resolveLocal(() => SEARCH_INDEX, { silent: true }),

      // Home page
      getHomeAbout: () => resolveLocal(() => [homeData.about]),
      getHomePopup: () => resolveLocal(() => [homeData.popup]),
      getHomeInformation: () => resolveLocal(() => [homeData.information]),
      getHomeNews: () => resolveLocal(() => sortByDateDesc(newsData).slice(0, 3).map(withNewsMedia)),
      getHomeEvents: () => resolveLocal(() => sortByDateDesc(homeData.events).slice(0, 4)),
      getAllUpdates: () => resolveLocal(() => sortByDateDesc(homeData.events)),
      getHomeFacts: () => resolveLocal(() => homeData.facts),
      getHomeCampusGallery: () => resolveLocal(() => homeData.campusGallery),
      getHomeSlider: () => resolveLocal(() => homeData.slider),
      // Homepage "Faculties & Departments" section — each faculty joined to
      // its published departments so the section needs a single call. The
      // per-department IsAcademic flag decides the target route the same way
      // buildSearchIndex / the nav menu do (/DptHome vs /AdmHome).
      getHomeFaculties: () =>
        resolveLocal(() =>
          facultyData.map((f) => ({
            FACULTY_ID: f.FACULTY_ID,
            FACULTY_NAME: f.FACULTY_NAME,
            DEPARTMENTS: departmentsData
              .filter((d) => d.FACULTY_ID === f.FACULTY_ID)
              .map((d) => ({
                DeptID: d.DeptID,
                DeptTitle: d.DeptTitle,
                IsAcademic: d.IsAcademic,
              })),
          }))
        ),
      // Homepage "Knowledge Resources" section — the Central Library record
      // from facilities.json (same source the /Facilities page and its
      // detail page read).
      getHomeLibrary: () => resolveLocal(() => facilitiesData.library || null),

      // News / Career / About / Contact
      getAllNews: () => resolveLocal(() => sortByDateDesc(newsData).map(withNewsMedia)),
      getNewsDetail: (slug) =>
        resolveLocal(() => {
          const story = newsData.find((n) => n.SLUG === slug);
          return story ? withNewsMedia(story) : null;
        }),
      getCareerListings: () => resolveLocal(() => CAREER_OPENINGS),
      getCareerDetail: (slug) => resolveLocal(() => CAREER_OPENINGS.find((job) => job.SLUG === slug) || null),
      // `formData` is a browser FormData instance (built by the caller) so the
      // CV travels as a real multipart file part. Do not set a Content-Type
      // header here — axios/the browser fills in the multipart boundary on
      // its own, and forcing one breaks the upload. Response/error shape is
      // interpreted by the caller (MSG === "Success", any other MSG, 429),
      // not here, so this stays a thin passthrough.
      submitCareerApplication: (formData) => axiosInstance.post("CAREER_APPLY", formData).then((res) => res.data),

      // Departments
      getDeptAbout: (id) => resolveLocal(() => (findDept(id) ? [findDept(id)] : [])),
      getDepartmentPrograms: (deptId) =>
        resolveLocal(() => programsData.filter((p) => p.DeptID === deptId), { silent: true }),
      getDeptDownloads: (id) => resolveLocal(() => findDept(id)?.DOWNLOADS || [], { silent: true }),
      getDeptEmployees: (id) => resolveLocal(() => findDept(id)?.EMPLOYEES || [], { silent: true }),
      getCourseScheme: (dptId) => resolveLocal(() => findDept(dptId)?.COURSE_SCHEME || [], { silent: true }),
      getDeptPlos: (id) => resolveLocal(() => findDept(id)?.PLOS || [], { silent: true }),
      getDeptPeos: (id) => resolveLocal(() => findDept(id)?.PEOS || [], { silent: true }),
      getDeptPublications: (id) => resolveLocal(() => findDept(id)?.PUBLICATIONS || [], { silent: true }),
      getDeptProjects: (id) => resolveLocal(() => findDept(id)?.PROJECTS || [], { silent: true }),
      getDeptEvents: (id) => resolveLocal(() => findDept(id)?.EVENTS || [], { silent: true }),
      getDeptSeminars: (id) => resolveLocal(() => findDept(id)?.SEMINARS || [], { silent: true }),

      // Faculty directory (per-department roster + individual profile pages)
      getFacultyByDepartment: (deptId) =>
        resolveLocal(
          () => (FACULTY_ROSTER_BY_SLUG[findDeptRaw(deptId)?.ROSTER_SLUG] || []).map((entry) => withPerson(entry)),
          { silent: true }
        ),
      getFacultyProfile: (slug) => resolveLocal(() => withPerson(FACULTY_PROFILE_BY_SLUG[slug]) || null),

      // Admission Policy 2026 content (officers, admission policy detail, facilities, fees)
      getOfficers: () =>
        resolveLocal(() => officersData.map((o) => withPerson(o, { nameField: "NAME", photoField: "PHOTO" }))),
      getLeadershipProfile: (slug) =>
        resolveLocal(() => {
          const officer = officersData.find((o) => o.profileSlug === slug);
          return officer ? withPerson(officer, { nameField: "NAME", photoField: "PHOTO" }) : null;
        }),
      getAcademicCalendar: () => resolveLocal(() => admissionPolicyData.academicCalendar),
      getEligibilityPolicy: () => resolveLocal(() => admissionPolicyData.eligibility),
      getPreAdmissionTest: () => resolveLocal(() => admissionPolicyData.preAdmissionTest),
      getAdmissionRules: () => resolveLocal(() => admissionPolicyData.rules),
      getFacilities: () => resolveLocal(() => facilitiesData),
      getFacilityDetail: (slug) =>
        resolveLocal(() => Object.values(facilitiesData).find((f) => f.SLUG === slug) || null),
      getFees: () => resolveLocal(() => feesData),
      getTestimonials: () => resolveLocal(() => testimonialsData),

      // Programs
      getProgramDetail: (pgmId) =>
        resolveLocal(() => {
          const program = programsData.find((p) => p.PGM_ID === pgmId);
          return program ? [program] : [];
        }),

      // Faculties
      getFacultyDetail: (id) =>
        resolveLocal(() => {
          const faculty = facultyData.find((f) => f.FACULTY_ID === id);
          if (!faculty) return [];
          return [faculty.DEAN ? { ...faculty, DEAN: withPerson(faculty.DEAN) } : faculty];
        }),

      // Admission
      getAdmissionHome: () => resolveLocal(() => [admissionData.home]),
      getAdmissionPolicy: () => resolveLocal(() => [admissionData.policy]),
      getFeeStructure: () => resolveLocal(() => [admissionData.feeStructure]),
      getAdmissionInstruction: () => resolveLocal(() => [admissionData.instruction]),
      getAdmissionPortal: () => resolveLocal(() => [admissionData.portal]),
      getSelfFinanceScheme: () => resolveLocal(() => [admissionData.selfFinance]),
      getSeatAllocation: () => resolveLocal(() => seatAllocationData),
    }),
    [resolveLocal]
  );

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};

// ---------------------------------------------------------------------------
// Reference: how each function body looked wired to the real backend, kept
// here so restoring the API path later is a direct copy-paste per function
// rather than a rebuild. `request()` lived where `resolveLocal()` is now:
//
//   const request = useCallback(async (functionName, body = {}, { silent = false } = {}) => {
//     try {
//       const { data } = await axiosInstance.post(functionName, body);
//       return data;
//     } catch (error) {
//       console.error(`API request failed: ${functionName}`, error);
//       if (!silent) toast.error("Something went wrong while loading data. Please try again.", { id: "api-error" });
//       throw error;
//     }
//   }, []);
//
//   getDeptName: (id) => request("DPT_NAME_GET", { ID: id }),
//   getDeptFacultyName: (id, typ) => request("DPT_FACULTY_NAME_GET", { ID: id, Typ: typ }),
//   getFacultyList: () => request("FACULTY_GET"),
//   getAdminDepartments: () => request("DPT_NAME_GET_ADMIN"),
//   getContactInfo: () => request("CONTACT_GET"),
//   getFacultyPrograms: (id) => request("FACULTY_PROGRAMS_GET", { ID: id }, { silent: true }),
//   getHomeAbout: () => request("HOME_ABOUT_GET"),
//   getHomePopup: () => request("HOME_POPUP_GET"),
//   getHomeInformation: () => request("DPT_INFORMATION_GET"),
//   getHomeNews: () => request("HOME_NEWS_GET"),
//   getHomeEvents: () => request("HOME_EVENT_GET"),
//   getHomeFacts: () => request("HOME_FACT_GET"),
//   getHomeCampusGallery: () => request("HOME_CAMPUS_GET"),
//   getHomeSlider: () => request("HOME_SLIDER_GET"),
//   getAllNews: () => request("HOME_NEWS_GET_ALL"),
//   getNewsDetail: (slug) => request("HOME_NEWS_GET_DETAIL", { SLUG: slug }),
//   getCareerListings: () => request("CAREER_GET_CMS"),
//   getCareerDetail: (slug) => request("CAREER_GET_DETAIL", { SLUG: slug }),
//   getDeptAbout: (id) => request("DPT_ABOUT_GET", { ID: id }),
//   getDepartmentPrograms: (deptId) => request("DEPARTMENT_PROGRAMS_GET", { DeptID: deptId }, { silent: true }),
//   getDeptDownloads: (id) => request("DPT_DOWNLOAD_GET", { ID: id }, { silent: true }),
//   getDeptEmployees: (id, catId) => request("DPT_EMP_GET", { ID: id, CatID: catId }, { silent: true }),
//   getCourseScheme: (dptId) => request("COURSE_SCHEME_GET_ID", { DPT_ID: dptId }, { silent: true }),
//   getDeptPlos: (id) => request("DPT_PLOS_GET", { ID: id }, { silent: true }),
//   getDeptPeos: (id) => request("DPT_PEOS_GET", { ID: id }, { silent: true }),
//   getDeptPublications: (id) => request("DPT_PUBLICATION_GET", { ID: id }, { silent: true }),
//   getDeptProjects: (id) => request("DPT_PROJECT_GET", { ID: id }, { silent: true }),
//   getDeptEvents: (id) => request("DPT_EVENT_GET", { ID: id }, { silent: true }),
//   getDeptSeminars: (id) => request("DPT_SEMINARS_GET", { ID: id }, { silent: true }),
//   getProgramDetail: (pgmId) => request("PROGRAM_GET_ALL", { PGM_ID: pgmId }),
//   getFacultyDetail: (id) => request("FACULTY_GET_ALL", { ID: id }),
//   getAdmissionHome: () => request("ADMISSION_HOME_GET"),
//   getAdmissionPolicy: () => request("ADMISSION_POLICY_GET"),
//   getAdmissionEligibility: () => request("ADMISSION_ELIGIBILITY_GET"),
//   getFeeStructure: () => request("FEE_STRUCTURE_GET"),
//   getAdmissionInstruction: () => request("ADMISSION_INSTRUCTION_GET"),
//   getAdmissionPortal: () => request("ADMISSION_PORTAL_GET"),
//   getSelfFinanceScheme: () => request("SELF_FINANCE_GET"),
// ---------------------------------------------------------------------------
